import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, get, post, put } from '../api';
import { askConfirm } from '../components/ask';
import { ComposeButtons, inviteLinks, type InviteCompose } from '../components/ComposeButtons';
import { Field, Problems } from '../components/Field';
import { Row } from '../components/FormRow';
import { currentLocale } from '../dates';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import { formatMoney } from '../money';
import { BILL_PILL, BILL_STATUS, payBill, unpayBill } from './billShared';

interface Detail {
  bill: {
    id: number;
    contractor_name: string;
    invoice_number: string;
    invoice_date: string | null;
    total_ex_gst: string;
    gst_amount: string;
    expected_ex_gst: string | null;
    status: string;
    paid_at: string | null;
    notes: string | null;
  };
  visits: {
    id: number;
    start: string;
    charge_amount: string;
    pay_amount: string;
    reason: string;
    customer_name: string | null;
  }[];
  /** For a bill that doesn't match: the contractor's other visits still to bill, one of which it may cover. */
  missed?: { id: number; start: string; pay_amount: string }[];
  /** A message asking the contractor about the difference. */
  ask?: { body: string } & InviteCompose;
}

const money = formatMoney;
const day = (iso: string) =>
  new Date(iso).toLocaleDateString(currentLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
const cents = (x: string | null) => Math.round(Number(x || 0) * 100);

/**
 * One contractor bill and the visits on it. Until it is paid, a visit can be
 * taken off (back to the pay queue), the total corrected, or the bill deleted;
 * a bill that doesn't match its visits says by how much and how to settle it.
 */
export function BillDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tick, setTick] = useState(0);
  const [problems, setProblems] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [justPaid, setJustPaid] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pays, setPays] = useState<Record<number, string>>({});
  const [adding, setAdding] = useState(0);
  const loaded = useLoad(() => get<Detail>(`/api/bills/${id}`), [id, tick]);

  async function act(run: () => Promise<string>) {
    setProblems([]);
    setMessage('');
    try {
      const said = await run();
      if (said) {
        setMessage(said);
        setTick((t) => t + 1);
      }
    } catch (e) {
      setProblems(describe(e).problems);
    }
  }

  if (loaded.loading && !loaded.data) return <p>Loading…</p>;
  if (!loaded.data) {
    return (
      <>
        <p role="alert" className="error">
          {loaded.error}
        </p>
        <Link to="/money?tab=bills">Back to bills</Link>
      </>
    );
  }
  const { bill, visits } = loaded.data;
  const paid = bill.status === 'paid';
  const disputed = bill.status === 'disputed';
  // Sent in from the portal: the office checks it before it is paid or sorted out.
  const toCheck = bill.status === 'received';
  const owed = visits.reduce((s, v) => s + cents(v.pay_amount), 0);
  const diff = cents(bill.total_ex_gst) - owed;

  const pay = () =>
    act(async () => {
      const said = await payBill(bill, disputed);
      if (said) setJustPaid(true);
      return said;
    });
  const undo = () =>
    act(async () => {
      await unpayBill(bill.id);
      setJustPaid(false);
      return 'Marked not paid. The bill is waiting to be paid again.';
    });
  const remove = () =>
    act(async () => {
      const yes = await askConfirm({
        title: `Delete bill ${bill.invoice_number}?`,
        body: `From ${bill.contractor_name}. Its visits go back to waiting to be billed.`,
        confirmLabel: 'Delete bill',
        danger: true,
      });
      if (!yes) return '';
      await api('DELETE', `/api/bills/${bill.id}`);
      nav('/money?tab=bills');
      return '';
    });
  const takeOff = (visitId: number) =>
    act(async () => {
      await post(`/api/bills/${id}/remove-visit`, { visit_id: visitId });
      return 'Taken off the bill. It is back in the pay queue.';
    });
  const addVisit = () =>
    act(async () => {
      await post(`/api/bills/${id}/add-visit`, { visit_id: adding });
      setAdding(0);
      return 'Added to the bill.';
    });
  // The visit's own pay changes, and the bill is checked against it again.
  const setPay = (v: Detail['visits'][number]) =>
    act(async () => {
      await put(`/api/visits/${v.id}/money`, { charge: v.charge_amount, pay: pays[v.id] });
      setPays((p) => Object.fromEntries(Object.entries(p).filter(([k]) => Number(k) !== v.id)));
      return `Pay for ${day(v.start)} changed to ${money(pays[v.id]!)}.`;
    });

  return (
    <div className="stack narrow">
      <p>
        <Link to="/money?tab=bills">← Bills</Link>
      </p>
      <div className="row start">
        <h1>Bill {bill.invoice_number}</h1>
        <span className={BILL_PILL[bill.status] ?? 'pill'}>
          {BILL_STATUS[bill.status] ?? bill.status}
        </span>
      </div>
      <Problems problems={problems} title="Could not do that." />
      {message && (
        <p className="notice row start" role="status">
          <span>{message}</span>
          {justPaid && paid && (
            <button type="button" className="quiet" onClick={() => void undo()}>
              Undo
            </button>
          )}
        </p>
      )}

      {toCheck && (
        <section className="callout" aria-labelledby="check-h">
          <h2 id="check-h">Sent in by {bill.contractor_name} from the portal</h2>
          <p>
            They billed {money(bill.total_ex_gst)}; the visits below pay {money(owed / 100)}
            {diff === 0
              ? ', so it matches.'
              : `: ${money(Math.abs(diff) / 100)} ${diff > 0 ? 'more' : 'less'}.`}{' '}
            Check the visits are theirs, then mark it checked: it becomes{' '}
            {diff === 0 ? 'ready to pay' : 'a bill that doesn’t match, to sort out here'}.
          </p>
          <div className="row start">
            <button
              type="button"
              onClick={() =>
                void act(async () => {
                  await post(`/api/bills/${bill.id}/check`);
                  return diff === 0
                    ? 'Checked: ready to pay.'
                    : 'Checked. It doesn’t match its visits yet.';
                })
              }
            >
              Checked
            </button>
          </div>
        </section>
      )}
      {disputed && !editing && (
        <section className="callout bad mismatch" aria-labelledby="diff-h">
          <h2 id="diff-h">
            The bill is {money(Math.abs(diff) / 100)} {diff > 0 ? 'more' : 'less'} than its visits
            pay
          </h2>
          <dl className="mismatch-figures">
            <div>
              <dt>{bill.contractor_name} billed</dt>
              <dd>{money(bill.total_ex_gst)}</dd>
            </div>
            <div>
              <dt>The visits pay</dt>
              <dd>{money(owed / 100)}</dd>
            </div>
            <div>
              <dt>Difference</dt>
              <dd>
                {money(Math.abs(diff) / 100)} {diff > 0 ? 'more' : 'less'}
              </dd>
            </div>
          </dl>
          <p>
            Sort it out below: correct a visit&rsquo;s pay, add a visit they billed for, or take off
            one that isn&rsquo;t theirs. If their invoice was wrong, correct its total; if
            you&rsquo;re not sure, ask them.
          </p>
          <div className="row start">
            <button type="button" onClick={() => setEditing(true)}>
              Correct their total
            </button>
            <button type="button" className="quiet" onClick={() => void pay()}>
              Accept their total
            </button>
          </div>
          {loaded.data.ask && (
            <details className="run-fold">
              <summary>Ask {bill.contractor_name.split(' ')[0]} about it</summary>
              <p className="notes">{loaded.data.ask.body}</p>
              <ComposeButtons
                items={inviteLinks(loaded.data.ask).map((i) => ({
                  ...i,
                  body: loaded.data!.ask!.body,
                }))}
              />
            </details>
          )}
        </section>
      )}
      {editing && (
        <EditTotal
          bill={bill}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            setMessage('Total updated.');
            setTick((t) => t + 1);
          }}
        />
      )}

      <section className="panel facts" aria-label="Bill">
        <Row label="Contractor">
          <span>{bill.contractor_name}</span>
        </Row>
        {bill.invoice_date && (
          <Row label="Bill date">
            <span>{day(`${bill.invoice_date}T12:00:00`)}</span>
          </Row>
        )}
        <Row label="Bill total ex GST">
          <span>{money(bill.total_ex_gst)}</span>
        </Row>
        <Row label="GST">
          <span>{money(bill.gst_amount)}</span>
        </Row>
        <Row label="Owed for these visits" hint="What the visits say the contractor is owed.">
          <span>{money(owed / 100)}</span>
        </Row>
        {paid && bill.paid_at && (
          <Row label="Paid">
            <span>{day(bill.paid_at)}</span>
          </Row>
        )}
        {bill.notes && (
          <Row label="Notes">
            <span className="notes">{bill.notes}</span>
          </Row>
        )}
      </section>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Customer</th>
              <th className="num">Pay</th>
              {!paid && (
                <th>
                  <span className="sr-only">Take off</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {visits.map((v) => (
              <tr key={v.id}>
                <td>
                  <Link to={`/visits/${v.id}`}>{day(v.start)}</Link>
                </td>
                <td>{v.customer_name}</td>
                <td className="num">
                  {disputed ? (
                    <span className="row end">
                      <input
                        className="num short"
                        inputMode="decimal"
                        aria-label={`Pay for ${day(v.start)}`}
                        value={pays[v.id] ?? v.pay_amount}
                        onChange={(e) => setPays({ ...pays, [v.id]: e.target.value })}
                      />
                      {pays[v.id] !== undefined && pays[v.id] !== v.pay_amount && (
                        <button type="button" className="quiet" onClick={() => void setPay(v)}>
                          Save<span className="sr-only"> pay for {day(v.start)}</span>
                        </button>
                      )}
                    </span>
                  ) : (
                    money(v.pay_amount)
                  )}
                </td>
                {!paid && (
                  <td className="actions">
                    <button type="button" className="quiet" onClick={() => void takeOff(v.id)}>
                      Take off bill<span className="sr-only"> for {day(v.start)}</span>
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {visits.length === 0 && <p className="empty">No visits are on this bill.</p>}
      {disputed && (loaded.data.missed?.length ?? 0) > 0 && (
        <div className="row start">
          <label>
            Add a visit they billed for{' '}
            <select value={adding} onChange={(e) => setAdding(Number(e.target.value))}>
              <option value={0}>Choose…</option>
              {loaded.data.missed!.map((m) => (
                <option key={m.id} value={m.id}>
                  {day(m.start)} · {money(m.pay_amount)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="quiet-button"
            disabled={adding === 0}
            onClick={() => void addVisit()}
          >
            Add to bill
          </button>
        </div>
      )}

      {!paid && (
        <div className="row start">
          {!disputed && !toCheck && (
            <button type="button" onClick={() => void pay()}>
              Mark paid
            </button>
          )}
          <button type="button" className="danger" onClick={() => void remove()}>
            Delete bill
          </button>
        </div>
      )}
    </div>
  );
}

/** Correct a bill's total and GST, e.g. after the contractor sends a fixed invoice. */
function EditTotal(props: { bill: Detail['bill']; onCancel(): void; onSaved(): void }) {
  const [total, setTotal] = useState(props.bill.total_ex_gst);
  const [gst, setGst] = useState(props.bill.gst_amount);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [problems, setProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setFields({});
    setProblems([]);
    try {
      await post(`/api/bills/${props.bill.id}/total`, { total_ex_gst: total, gst_amount: gst });
      props.onSaved();
    } catch (e) {
      const d = describe(e);
      setFields(d.fields);
      setProblems(d.problems);
      setBusy(false);
    }
  }

  return (
    <section className="panel" aria-labelledby="edit-total-h">
      <h2 id="edit-total-h">Correct the total</h2>
      <Problems problems={problems} title="Not saved." />
      <div className="stack-tight">
        <Field label="Total ex GST" error={fields.total_ex_gst}>
          <input
            className="num"
            inputMode="decimal"
            value={total}
            onChange={(e) => setTotal(e.target.value)}
          />
        </Field>
        <Field label="GST on the bill" error={fields.gst_amount}>
          <input
            className="num"
            inputMode="decimal"
            value={gst}
            onChange={(e) => setGst(e.target.value)}
          />
        </Field>
        <div className="row start">
          <button type="button" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save total'}
          </button>
          <button type="button" className="quiet" onClick={props.onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  );
}
